import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Input, Tag, Dropdown, Badge, theme, App, Typography } from 'antd';
import type { TableProps } from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  FilterOutlined,
  MoreOutlined,
  EditOutlined,
  CheckCircleOutlined,
  StopOutlined,
  InboxOutlined,
} from '@ant-design/icons';

const { Title } = Typography;
import { useCategoriesTable, useCategoryMutations } from '@/hooks';
import type { CategoryListItem } from '@/types/api/category';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useCategoryStore } from '@/stores';
import CategoryFormModal from './CategoryFormModal';
import CategoryFilterPopover from './CategoryFilterPopover';
import DeactivateCategoryModal from './DeactivateCategoryModal';
import { ConfirmationDialog, useConfirmationDialog } from '@/components/common/ConfirmationDialog';
import TableContainer from '@/components/common/TableContainer';
import CategoryTag from '@/components/common/CategoryTag';
import { useVendorTranslation } from '@/hooks/useVendorTranslation';
import { TimestampDisplay } from '@/components/common/TimestampDisplay';
import { HighlightText } from '@/components/common/HighlightText';

interface CategoryListPageProps {
  compact?: boolean; // If true, hide header and filters
  showTypeColumn?: boolean; // If false, hide type column
}

const CategoryListPage = ({
  compact = false,
  showTypeColumn = true,
}: CategoryListPageProps = {}) => {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const { t } = useVendorTranslation();

  // Use Zustand store for filters
  const { filters, setSearch, setTypeIds, setIsActive } = useCategoryStore();

  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryListItem | null>(null);
  const [toggling, setToggling] = useState(false);
  const [deactivateModalVisible, setDeactivateModalVisible] = useState(false);
  const [categoryToDeactivate, setCategoryToDeactivate] = useState<CategoryListItem | null>(null);

  // Fetch categories using useAntdTable hook - returns tableProps for direct Table integration
  const { tableProps, isLoading, error, refresh } = useCategoriesTable({
    search: filters.search,
    typeIds: filters.typeIds,
    isActive: filters.isActive,
  });
  const { toggleStatus } = useCategoryMutations();

  // Confirmation dialog for activate category only (deactivate uses separate modal)
  const toggleStatusDialog = useConfirmationDialog<CategoryListItem>();

  // Handle filter apply
  const handleFilterApply = (filterValues: { typeIds: number[]; isActive: boolean | null }) => {
    setTypeIds(filterValues.typeIds);
    setIsActive(filterValues.isActive);
  };

  // Handle toggle status with confirmation
  const handleToggleStatus = (category: CategoryListItem) => {
    if (category.isActive) {
      // Deactivating - use the new DeactivateCategoryModal
      setCategoryToDeactivate(category);
      setDeactivateModalVisible(true);
    } else {
      // Activating - use simple confirmation dialog
      toggleStatusDialog.open(category);
    }
  };

  const baseColumns: TableProps<CategoryListItem>['columns'] = [
    {
      title: t('vendor.fields.categoryName'),
      dataIndex: 'category',
      key: 'category',
      width: 200,
      sorter: true,
      showSorterTooltip: false,
      render: (category: string, record: CategoryListItem) => (
        <CategoryTag name={category} color={record.color || token.colorPrimary} />
      ),
    },
    {
      title: t('vendor.fields.categoryType'),
      dataIndex: 'typeInfo',
      key: 'typeInfo',
      width: 180,
      sorter: false,
      render: (typeInfo: CategoryListItem['typeInfo']) => {
        if (!typeInfo || typeInfo.length === 0) return '-';

        // Show all types (at most 2: Vendor/Customer)
        return (
          <div className="tw-flex tw-gap-1">
            {typeInfo.map((item) => (
              <Tag
                key={item.typeId}
                bordered={false}
                color="purple"
                style={{
                  borderRadius: radiusTokens.control,
                }}
              >
                {item.type}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: t('vendor.fields.notes'),
      dataIndex: 'description',
      key: 'description',
      width: 400,
      render: (description: string) => <HighlightText text={description} searchTerm={filters.search} fallback="-" />,
    },
    {
      title: t('common:createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 130,
      render: (createdAt: string) =>
        createdAt ? <TimestampDisplay date={createdAt} showLabel={false} /> : '-',
    },
    {
      title: t('common:updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 130,
      render: (updatedAt: string) =>
        updatedAt ? <TimestampDisplay date={updatedAt} showLabel={false} /> : '-',
    },
    {
      title: t('vendor.fields.action'),
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_: unknown, record: CategoryListItem) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'edit',
                label: t('vendor.actions.edit'),
                icon: <EditOutlined />,
                disabled: !record.isActive, // Disable edit for inactive categories
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  if (record.isActive) {
                    setEditingCategory(record);
                    setModalVisible(true);
                  }
                },
              },
              {
                type: 'divider',
              },
              {
                key: 'toggle-status',
                label: record.isActive
                  ? t('vendor.actions.deactivate')
                  : t('vendor.actions.activate'),
                icon: record.isActive ? <StopOutlined /> : <CheckCircleOutlined />,
                danger: record.isActive === true,
                style: !record.isActive ? { color: token.colorSuccess } : undefined,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  handleToggleStatus(record);
                },
              },
            ],
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
        </Dropdown>
      ),
    },
  ];

  // Filter columns based on props
  const allColumns = showTypeColumn
    ? baseColumns
    : baseColumns.filter((col) => col.key !== 'typeInfo');

  // Count active filters
  // - typeIds: count each selected type
  // - isActive: count only when NOT default (true = Active is default, don't count)
  const activeFilterCount = (filters.typeIds?.length || 0) + (filters.isActive !== true ? 1 : 0);

  return (
    <div className={compact ? '' : 'tw-px-6 tw-pb-8'}>
      {/* Production Enhanced Table Styling */}
      <style>
        {`
          /* Production Enhanced - Tabular numbers for all tables */
          .production-enhanced .ant-table-cell {
            font-variant-numeric: tabular-nums;
            font-feature-settings: 'tnum';
          }

          /* Consistent 40px row height */
          .production-enhanced .ant-table-tbody > tr > td {
            height: 40px;
            min-height: 40px;
          }

          /* Title case headers (no transformation) */
          .production-enhanced .ant-table-thead > tr > th {
            font-weight: ${token.fontWeightStrong};
          }

          /* Inactive row styling - uniform dimmed appearance */
          /* Must target both tr AND td because Ant Design sets background on cells */
          .production-enhanced .ant-table-tbody > tr.inactive-row,
          .production-enhanced .ant-table-tbody > tr.inactive-row > td {
            background-color: ${token.colorBgLayout} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row:hover > td,
          .production-enhanced .ant-table-tbody > tr.inactive-row:active > td,
          .production-enhanced .ant-table-tbody > tr.inactive-row:focus > td {
            background-color: ${token.colorFillQuaternary} !important;
          }

          /* Individual cell hover - match row hover */
          .production-enhanced .ant-table-tbody > tr.inactive-row > td:hover {
            background-color: ${token.colorFillQuaternary} !important;
          }

          /* Keep background when dropdown is open */
          .production-enhanced .ant-table-tbody > tr.inactive-row.ant-dropdown-open > td,
          .production-enhanced .ant-table-tbody > tr.inactive-row.ant-dropdown-open > td:hover {
            background-color: ${token.colorBgLayout} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row td {
            color: ${token.colorTextTertiary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row a {
            color: ${token.colorTextTertiary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn,
          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn:hover,
          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn:focus,
          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn:active {
            color: ${token.colorTextTertiary} !important;
            background: transparent !important;
            border-color: transparent !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row .anticon {
            color: ${token.colorTextTertiary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn .anticon {
            color: ${token.colorTextTertiary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-tag {
            opacity: 0.5;
            filter: grayscale(100%);
          }

          /* Gray out category color dots in inactive rows */
          .production-enhanced .ant-table-tbody > tr.inactive-row div[style*="border-radius: 50%"],
          .production-enhanced .ant-table-tbody > tr.inactive-row div[style*="borderRadius"] {
            background-color: ${token.colorTextTertiary} !important;
            opacity: 0.5;
          }
        `}
      </style>

      {/* Header Section */}
      {!compact && (
        <div className="tw-mb-6">
          <h1 style={{ fontSize: token.fontSizeHeading2, fontWeight: token.fontWeightStrong }}>
            {t('vendor.categories.list.title')}
          </h1>
        </div>
      )}

      {/* Search and Filter Section */}
      {!compact && (
        <div className="tw-mb-6 tw-flex tw-gap-3 tw-items-center">
          <Input
            placeholder={t('vendor.categories.list.searchPlaceholder')}
            value={filters.search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            size="large"
            suffix={
              <SearchOutlined
                style={{
                  fontSize: token.sizeMD,
                  color: token.colorPrimary,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
              />
            }
            style={{
              width: '450px',
              borderRadius: radiusTokens.card,
              height: `${controlHeightTokens.controlHeightXL}px`,
              transition: 'all 0.3s ease',
            }}
            styles={{
              input: {
                borderRadius: radiusTokens.card,
              },
            }}
          />
          <CategoryFilterPopover
            open={filterPopoverOpen}
            onOpenChange={setFilterPopoverOpen}
            onApply={handleFilterApply}
            initialFilters={{
              typeIds: filters.typeIds || [],
              isActive: filters.isActive,
            }}
          >
            <Badge count={activeFilterCount}>
              <Button
                icon={<FilterOutlined style={{ color: token.colorPrimary }} />}
                size="large"
                onClick={() => setFilterPopoverOpen(true)}
                style={{
                  borderRadius: radiusTokens.card,
                  height: `${controlHeightTokens.controlHeightXL}px`,
                  width: `${controlHeightTokens.controlHeightXL}px`,
                  border: `1px solid ${token.colorBorder}`,
                  transition: 'all 0.3s ease',
                }}
                styles={{
                  icon: { fontSize: token.sizeMD },
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = token.colorPrimary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = token.colorBorder;
                }}
              />
            </Badge>
          </CategoryFilterPopover>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => {
              setEditingCategory(null);
              setModalVisible(true);
            }}
            style={{
              marginLeft: 'auto',
              borderRadius: radiusTokens.card,
              height: `${controlHeightTokens.controlHeightXL}px`,
              paddingLeft: token.paddingLG,
              paddingRight: token.paddingLG,
            }}
          >
            {t('vendor.categories.list.create')}
          </Button>
        </div>
      )}

      {/* Table - using tableProps from useAntdTable for automatic pagination/sorting */}
      <TableContainer>
        <Table<CategoryListItem>
          {...tableProps}
          columns={allColumns}
          rowKey="categoryId"
          rowClassName={(record) => (!record.isActive ? 'inactive-row' : '')}
          loading={isLoading}
          showSorterTooltip={false}
          onRow={(record) => ({
            onClick: () => {
              navigate(`/auth/categories/${record.categoryId}`);
            },
            style: {
              cursor: 'pointer',
            },
          })}
          locale={{
            emptyText: (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: `${token.paddingXL * 2}px ${token.paddingXL}px`,
                  textAlign: 'center',
                }}
              >
                <InboxOutlined
                  style={{
                    fontSize: token.sizeXXL * 2,
                    color: token.colorTextDisabled,
                    marginBottom: token.marginLG,
                  }}
                />
                <Title
                  level={3}
                  style={{
                    marginBottom: filters.search || activeFilterCount > 0 ? 0 : token.marginLG,
                    color: token.colorText,
                  }}
                >
                  {filters.search || activeFilterCount > 0
                    ? t('vendor.categories.list.emptyWithFiltersTitle')
                    : t('vendor.categories.list.emptyNoDataTitle')}
                </Title>
                {!filters.search && activeFilterCount === 0 && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="large"
                    style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
                    onClick={() => {
                      setEditingCategory(null);
                      setModalVisible(true);
                    }}
                  >
                    {t('vendor.categories.list.createFirst')}
                  </Button>
                )}
              </div>
            ),
          }}
          pagination={{
            ...tableProps.pagination,
            showSizeChanger: false,
          }}
          scroll={{ x: 1000 }}
        />
      </TableContainer>

      {/* Show error if fetch failed */}
      {error && (
        <div style={{ marginTop: token.margin, color: token.colorError }}>
          {t('vendor.categories.list.fetchFailed')}: {error.message}
        </div>
      )}

      {/* Category Form Modal */}
      <CategoryFormModal
        visible={modalVisible}
        onClose={() => {
          setModalVisible(false);
          setEditingCategory(null);
        }}
        onSuccess={() => {
          // Cache is already invalidated by the mutation, refresh to show updates
          refresh();
          setEditingCategory(null);
        }}
        category={editingCategory}
      />

      {/* Confirmation Dialog for Activate Only */}
      <ConfirmationDialog
        open={toggleStatusDialog.isOpen}
        onClose={toggleStatusDialog.close}
        onConfirm={async () => {
          const categoryData = toggleStatusDialog.data;
          if (categoryData) {
            setToggling(true);
            try {
              await toggleStatus(categoryData.categoryId, true);
              message.success(
                t('vendor.messages.categoryActionSuccess', {
                  action: t('vendor.status.active').toLowerCase(),
                })
              );
              await refresh();
              toggleStatusDialog.close();
            } catch (error) {
              message.error(t('vendor.messages.statusUpdateFailed'));
              console.error('Error activating category:', error);
            } finally {
              setToggling(false);
            }
          }
        }}
        title={t('vendor.notifications.activateCategory')}
        message={t('vendor.messages.confirmStatusChange', {
          action: t('vendor.actions.activate').toLowerCase(),
          name: toggleStatusDialog.data?.category || '',
        })}
        type="info"
        confirmText={t('vendor.actions.activate')}
        loading={toggling}
      />

      {/* Deactivate Category Modal with Batch Replace */}
      <DeactivateCategoryModal
        visible={deactivateModalVisible}
        onClose={() => {
          setDeactivateModalVisible(false);
          setCategoryToDeactivate(null);
        }}
        onSuccess={() => {
          refresh();
          setCategoryToDeactivate(null);
        }}
        category={categoryToDeactivate}
      />
    </div>
  );
};

export default CategoryListPage;
